import { forwardRef, ReactNode, RefObject, useState } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '@components/ui/popover';
import { Calendar } from '@components/ui/calendar';
import { cn } from '@utils/cn';
import { SvgConsumer } from '@components/icons/svgConsumer';
import { Button } from '@components/ui/button';
import { isBefore, isEqual, isToday, startOfDay, startOfToday } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { CustomDateRange } from '@components/ui/calendar/types';
import { Matcher } from 'react-day-picker';
import { TZDate } from '@date-fns/tz';
import { fromCalendarDay, inTenantZone, tenantNow, toCalendarDay } from '@utils/timeZone';

type BasicDatePickerProps = {
  error?: string;
  placeholder?: string;
  disabled?: boolean;
  readOnly?: boolean;
  className?: string;
  noText?: boolean;
  noRemove?: boolean;
  open?: boolean;
  setOpen?: (value: boolean) => void;
  children?: ReactNode;
  popoverRef?: RefObject<HTMLElement | null>;
  disablePast?: boolean;
  minDate?: Date;
  disabledDays?: Matcher[];
  notDrawer?: boolean;
  modifiers?: Record<string, Matcher | Matcher[]>;
  modifiersClassNames?: Record<string, string>;
  legend?: ReactNode;
  todayDisabled?: boolean;
};

export type DatePickerProps = (
  | ({ mode: 'single' } & {
      value?: Date | null;
      onChange?: (value?: Date | null) => void;
    })
  | ({ mode: 'range' } & { value?: CustomDateRange; onChange?: (value?: CustomDateRange) => void })
) &
  BasicDatePickerProps;

const DatePicker = forwardRef<HTMLInputElement, DatePickerProps>(
  (
    {
      mode,
      placeholder,
      value,
      onChange,
      error,
      disabled,
      readOnly,
      className = '',
      noText = false,
      noRemove = false,
      children,
      open,
      setOpen,
      popoverRef,
      disablePast,
      minDate,
      disabledDays,
      notDrawer = true,
      modifiers,
      modifiersClassNames,
      legend,
      todayDisabled,
    },
    ref,
  ) => {
    const minDay = minDate instanceof TZDate ? toCalendarDay(minDate) : minDate;
    const disabledMatchers = [...(minDay ? [{ before: minDay }] : []), ...(disabledDays || [])];
    const singleValue = mode === 'single' ? value : undefined;
    const zoned = singleValue instanceof TZDate;
    const calendarValue = zoned ? toCalendarDay(singleValue) : singleValue;
    const { t } = useTranslation('app', { keyPrefix: 'actions' });
    const [state, setState] = useState(false);
    const [isOpen, setIsOpen] =
      typeof open === 'boolean' && setOpen ? [open, setOpen] : [state, setState];

    return (
      <Popover open={isOpen} onOpenChange={!disabled ? setIsOpen : undefined}>
        <PopoverTrigger asChild>
          {children ? (
            children
          ) : (
            <div
              className={cn(
                'group-[.error]:shadow-danger-b flex h-8 w-full items-center gap-2 rounded border border-neutral-300 bg-white px-3 text-m text-neutral-800 focus-within:border-primary-600 focus-within:outline focus-within:outline-2 focus-within:outline-primary-200',
                error ? 'border-danger-500 focus-within:border-danger-500' : '',
                disabled ? 'border-neutral-200 bg-neutral-50 text-neutral-400' : '',
                readOnly ? 'border-neutral-300 text-neutral-800' : '',
                className,
              )}>
              <button
                className="flex w-full items-center justify-between outline-transparent disabled:cursor-not-allowed"
                type="button"
                disabled={disabled}>
                {!noText && (
                  <>
                    {!value && <span className="text-neutral-400">{placeholder}</span>}
                    {value && mode === 'single' && value.toLocaleDateString()}
                    {mode === 'range' && (
                      <span>
                        {value?.from && value.from.toLocaleDateString()}
                        {value?.to && ' - '}
                        {value?.to && value.to.toLocaleDateString()}
                      </span>
                    )}
                  </>
                )}
                {!noRemove &&
                ((mode === 'single' && value) || (mode === 'range' && value?.from)) ? (
                  <button
                    className="text-sm text-neutral-400 hover:text-danger-500 disabled:text-neutral-200"
                    disabled={disabled}
                    onClick={(e) => {
                      if (!onChange) return;
                      e.stopPropagation();
                      if (mode === 'range') onChange({ from: undefined });
                      else onChange(null);
                    }}
                    type="button">
                    <SvgConsumer id="trash" />
                  </button>
                ) : (
                  <SvgConsumer
                    id="calendar"
                    className={cn([
                      'h-3.5 w-auto shrink-0 hover:text-neutral-500',
                      noText ? 'text-inherit' : 'text-neutral-300',
                      !disabled ? 'text-neutral-300' : 'text-neutral-200',
                      isOpen && 'text-neutral-500',
                    ])}
                  />
                )}
              </button>
            </div>
          )}
        </PopoverTrigger>
        <PopoverContent ref={ref} container={popoverRef?.current} className="w-auto p-0">
          {mode === 'range' ? (
            <Calendar
              mode={mode}
              defaultMonth={value?.from}
              numberOfMonths={2}
              selected={value}
              setOpen={setIsOpen}
              onChange={onChange}
              range={value?.range}
              onSelect={(v) => {
                if (!onChange) return;
                const wasEmpty = !value?.from && !value?.to;
                if (!wasEmpty && v?.from && v?.to && isEqual(v.from, v.to)) return;
                onChange(v);
              }}
              autoFocus
              disablePast={disablePast}
              notDrawer={notDrawer}
            />
          ) : (
            <Calendar
              mode={mode}
              selected={calendarValue || undefined}
              defaultMonth={
                minDay
                  ? calendarValue || minDay
                  : modifiers
                    ? calendarValue || undefined
                    : undefined
              }
              modifiers={modifiers}
              modifiersClassNames={modifiersClassNames}
              setOpen={setIsOpen}
              {...(disabledMatchers.length && { disabled: disabledMatchers })}
              onSelect={(v) => {
                if (!onChange) return;
                if (v) onChange(zoned ? fromCalendarDay(v) : v);
                setIsOpen(false);
              }}
              autoFocus
              disablePast={disablePast}
              footer={
                value &&
                isOpen &&
                !(zoned ? isToday(value, { in: inTenantZone() }) : isToday(value)) &&
                !disabledDays?.length &&
                !(minDay && isBefore(startOfToday(), startOfDay(minDay))) && (
                  <Button
                    variant="secondary"
                    disabled={todayDisabled}
                    onClick={() => {
                      if (!onChange) return;
                      onChange(zoned ? tenantNow() : new Date());
                      setIsOpen(false);
                    }}
                    className="mt-3 w-full"
                    size="sm">
                    {t('backToToday')}
                  </Button>
                )
              }
            />
          )}
          {mode === 'single' && legend}
        </PopoverContent>
      </Popover>
    );
  },
);

export { DatePicker };
